import { Badge, Button } from "react-bootstrap";
import type { SecurityCategorySuggestion } from "../../../api/src/security-suggestions/security-suggestions.service";

/** One symbol's suggestion as the screen holds it while it is pending. */
export type PendingSuggestion = SecurityCategorySuggestion;

export type SuggestionField = "category" | "securityType";

/**
 * The errors the route gives one symbol when the model answered the request
 * but not that symbol (`mergeSuggestions` in
 * `api/src/security-suggestions/security-suggestions.service.ts`). Every other
 * error text comes from a fault of the whole model call. A new error of one
 * symbol in `mergeSuggestions` must be added here too, or the screen sends that
 * row again.
 */
export const ERRORS_OF_ONE_SYMBOL: readonly string[] = [
  "No suggestion was returned.",
  "No valid suggestion was returned.",
];

const FIELD_LABEL: Record<SuggestionField, string> = {
  category: "category",
  securityType: "security type",
};

/**
 * The suggested value for one field of one row, shown beside the current
 * value with accept and reject buttons. Nothing changes until accept.
 *
 * `differs` highlights a suggestion that would change the current value, for
 * example an ETF recorded as a Mutual Fund. Without `onAccept` and `onReject`
 * the value is shown with no buttons.
 */
export function SuggestedValue({
  symbol,
  field,
  value,
  differs,
  confidence,
  rationale,
  acceptDisabled,
  rejectDisabled,
  onAccept,
  onReject,
}: {
  symbol: string;
  field: SuggestionField;
  value: string;
  differs: boolean;
  confidence: PendingSuggestion["confidence"];
  rationale: string | null;
  acceptDisabled?: boolean;
  rejectDisabled?: boolean;
  onAccept?: () => void;
  onReject?: () => void;
}) {
  const label = FIELD_LABEL[field];
  return (
    <div
      className={`d-flex align-items-center gap-1 mt-1 small ${
        differs ? "text-warning-emphasis" : "text-muted"
      }`}
      data-testid={`suggestion-${field}-${symbol}`}
      data-differs={differs ? "true" : "false"}
      title={rationale ?? undefined}
    >
      <Badge
        bg={differs ? "warning" : "light"}
        text="dark"
        className="fw-normal"
      >
        Suggested: {value}
      </Badge>
      {confidence && <span className="text-muted">({confidence})</span>}
      {onAccept && (
        <Button
          size="sm"
          variant="link"
          className="p-0 text-success"
          aria-label={`Accept suggested ${label} ${value} for ${symbol}`}
          disabled={acceptDisabled}
          onClick={onAccept}
        >
          ✓
        </Button>
      )}
      {onReject && (
        <Button
          size="sm"
          variant="link"
          className="p-0 text-danger"
          aria-label={`Reject suggested ${label} for ${symbol}`}
          disabled={rejectDisabled}
          onClick={onReject}
        >
          ✗
        </Button>
      )}
    </div>
  );
}

/** Why no suggestion could be made for a row. */
export function SuggestionError({ message }: { message: string }) {
  return <div className="small text-danger mt-1">{message}</div>;
}
